"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PagedDataSource = void 0;
var EqualityComparers_1 = require("icf-search-client-shared/lib/EqualityComparers");
var observables_1 = require("icf-search-client-shared/lib/observables");
var Subscribable_1 = require("icf-search-client-shared/lib/observables/Subscribable");
var Utilities_1 = require("icf-search-client-shared/lib/Utilities");
var PageCache_1 = require("./PageCache");
var PagedDataSource = /** @class */ (function () {
    function PagedDataSource(itemRange, dataProvider, pageSize, pageCacheTtlMs, dataUpdateProvider) {
        var _this = this;
        this._resultsContext = new observables_1.Observable({
            totalItemCount: 0,
            isLoadingInitialResults: true,
            isFallback: false,
            errors: [],
        }, EqualityComparers_1.deepEqualityComparer);
        this._itemRange = new observables_1.Observable(itemRange, EqualityComparers_1.rangeEqualityComparer);
        this._itemRange.subscribe(function () {
            // Changes to the item range needs to immediately fire items changed.
            _this._itemsChanged.notifySubscribers();
        });
        this._pageSize = pageSize;
        this._itemsChanged = new Subscribable_1.Subscribable();
        this._pageCache = new PageCache_1.PageCache(dataProvider, pageSize, dataUpdateProvider, pageCacheTtlMs);
        this._pageCache.pageRequestStarted.subscribe(function (mostRecentPage) {
            setTimeout(function () {
                _this._updateResultsContext(mostRecentPage);
                _this._itemsChanged.notifySubscribers();
            });
        });
        this._pageCache.pageRequestCompleted.subscribe(function (mostRecentPage) {
            setTimeout(function () {
                _this._updateResultsContext(mostRecentPage);
                _this._itemsChanged.notifySubscribers();
            });
        });
        this._itemCount = new observables_1.ComputedObservable(function () {
            if (!_this._pageCache.hasInitialResults.value) {
                return _this._itemRange.value.end - _this._itemRange.value.start;
            }
            var offsetTotalCount = Math.max(_this._pageCache.totalItemCount.value - _this._itemRange.value.start, 0);
            return Math.min(offsetTotalCount, _this._itemRange.value.end - _this._itemRange.value.start);
        }, [
            this._pageCache.totalItemCount,
            this._pageCache.totalOnlineItemCount,
            this._itemRange,
            this._pageCache.hasInitialResults,
        ]);
        this._updateResultsContext();
    }
    Object.defineProperty(PagedDataSource.prototype, "hasInitialResults", {
        get: function () {
            return this._pageCache.hasInitialResults;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "itemRange", {
        get: function () {
            return this._itemRange;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "totalItemCount", {
        get: function () {
            return this._pageCache.totalItemCount;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "totalOnlineItemCount", {
        get: function () {
            return this._pageCache.totalOnlineItemCount;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "itemCount", {
        get: function () {
            return this._itemCount;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "itemsChanged", {
        get: function () {
            return this._itemsChanged;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PagedDataSource.prototype, "resultsContext", {
        get: function () {
            return this._resultsContext.asReadonly();
        },
        enumerable: false,
        configurable: true
    });
    PagedDataSource.prototype.requestDataUpdate = function (data) {
        return this._pageCache.updateData(data);
    };
    PagedDataSource.prototype.getItemsInRange = function (range) {
        var _this = this;
        var relativeRange = {
            start: range.start + this._itemRange.value.start,
            end: Math.min(range.end + this._itemRange.value.start, this._itemRange.value.end),
        };
        // The range of the iterator is capped by either the optimistic count or the known count after initial results
        // are fetched.
        var resultsContext = this._resultsContext.value;
        if (resultsContext && !resultsContext.isLoadingInitialResults) {
            relativeRange.end = Math.min(relativeRange.end, resultsContext.totalItemCount);
        }
        return (0, Utilities_1.iterateOverChunksIter)(relativeRange, this._pageSize, function (chunkIndex, startPosition) {
            var page = _this._pageCache.getPage(chunkIndex);
            if (!(page === null || page === void 0 ? void 0 : page.pageResults)) {
                return (0, Utilities_1.valuesIter)(new Array(_this._pageSize - startPosition));
            }
            return (0, Utilities_1.valuesIter)(page.pageResults.results.slice(startPosition));
        });
    };
    PagedDataSource.prototype.dispose = function () {
        this._itemCount.dispose();
        this._itemsChanged.dispose();
        this._pageCache.dispose();
        this._itemRange.dispose();
        this._resultsContext.dispose();
    };
    PagedDataSource.prototype.reset = function () {
        this._pageCache.reset();
        this._updateResultsContext();
        this._itemsChanged.notifySubscribers();
    };
    PagedDataSource.prototype._updateResultsContext = function (mostRecentPage) {
        var _a, _b, _c, _d, _e;
        var currentError = (mostRecentPage === null || mostRecentPage === void 0 ? void 0 : mostRecentPage.pagePromise.error.value) ? mostRecentPage.pagePromise.error.value : null;
        var errors = (_b = (_a = mostRecentPage === null || mostRecentPage === void 0 ? void 0 : mostRecentPage.pageResults) === null || _a === void 0 ? void 0 : _a.errors) !== null && _b !== void 0 ? _b : [];
        this._resultsContext.value = {
            totalItemCount: this.totalItemCount.value,
            totalOnlineItemCount: this.totalOnlineItemCount.value,
            isLoadingInitialResults: !this.hasInitialResults.value && !currentError,
            isFallback: !!((_c = mostRecentPage === null || mostRecentPage === void 0 ? void 0 : mostRecentPage.pageResults) === null || _c === void 0 ? void 0 : _c.isFallback),
            requestedIndex: (_d = mostRecentPage === null || mostRecentPage === void 0 ? void 0 : mostRecentPage.pageResults) === null || _d === void 0 ? void 0 : _d.index,
            errors: errors,
            currentError: currentError || ((_e = mostRecentPage === null || mostRecentPage === void 0 ? void 0 : mostRecentPage.pageResults) === null || _e === void 0 ? void 0 : _e.currentError),
        };
    };
    return PagedDataSource;
}());
exports.PagedDataSource = PagedDataSource;
//# sourceMappingURL=PagedDataSource.js.map